import { CalendarClock, ExternalLink, MapPinned } from 'lucide-react';
import logo from '../../assets/logo/logo_banco_alimentare.svg';
import {
  decodeImgSrc,
  getContrastText,
  getLeafFields,
  getOptionLabel,
  getVisibleOptions,
  isFieldVisible,
  normalizeField,
  normalizeInvitation,
  stripHtml,
  hasRichHtmlMarkup,
  sanitizeDescriptionHtml,
  descriptionToPlainText,
  isEmptyRichText,
  hasInvitationLocation,
  formatEventDateLabel,
  DEFAULT_SUCCESS_MESSAGE,
  DEFAULT_CLOSED_MESSAGE,
  locationHasMapMarkers,
} from '../../lib/invitation';
import InvitationMap from './InvitationMap';
import InvitationPrivacyNotice from './PrivacyPolicyNotice';

function formatSummaryValue(value) {
  if (typeof value === 'boolean') return value ? 'Sì' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  return value || '—';
}

function hasSummaryValue(value) {
  if (typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.length > 0;
  return value !== '' && value != null;
}

function mapsExternalUrl(location) {
  if (!location) return null;
  if (location.lat != null && location.lng != null) {
    return `https://www.openstreetmap.org/?mlat=${location.lat}&mlon=${location.lng}#map=16/${location.lat}/${location.lng}`;
  }
  if (location.address?.trim()) {
    return `https://www.openstreetmap.org/search?query=${encodeURIComponent(location.address.trim())}`;
  }
  return null;
}

/**
 * Pick responsive classes for live preview.
 * Media queries use the browser viewport, so a narrow "phone frame" on desktop
 * still matches md/sm. When viewport is forced, apply mobile or desktop classes
 * without relying on the browser width.
 * Pass full Tailwind class strings (literals) so JIT can detect them.
 */
function responsiveClass(viewport, { mobile, desktop, auto }) {
  if (viewport === 'mobile') return mobile;
  if (viewport === 'desktop') return desktop;
  return auto;
}

function InvitationPreview({
  invitation: rawInvitation,
  mode = 'preview',
  formValues = {},
  onFieldChange,
  onCheckboxChange,
  onSubmit,
  submitted = false,
  submittedSummary = null,
  closed = false,
  closedMessage,
  compact = false,
  disableSubmit = false,
  readOnly = false,
  resizeMapTrigger,
  /** 'auto' | 'mobile' | 'desktop' — force layout for embedded live preview */
  viewport = 'auto',
}) {
  const invitation = normalizeInvitation(rawInvitation);
  if (!invitation) return null;

  const { theme, location } = invitation;
  const primaryColor = invitation.color1;
  const secondaryColor = invitation.color2;
  const buttonTextColor = getContrastText(secondaryColor);
  const logoSrc = decodeImgSrc(invitation.logo);
  const coverSrc = decodeImgSrc(invitation.image);
  const safeTitle = stripHtml(invitation.name) || 'Titolo invito';
  const descriptionRaw = invitation.description || '';
  const descriptionIsRich = hasRichHtmlMarkup(descriptionRaw);
  const safeDescriptionHtml = descriptionIsRich ? sanitizeDescriptionHtml(descriptionRaw) : '';
  const safeDescriptionPlain = descriptionIsRich ? '' : descriptionToPlainText(descriptionRaw);
  const hasDescription = !isEmptyRichText(descriptionRaw);
  const showCover = theme.coverStyle !== 'none' && (coverSrc || mode === 'preview');
  const eventDateLabel = formatEventDateLabel(invitation.eventDate);
  const showLocation = hasInvitationLocation(location);
  const mapsUrl = mapsExternalUrl(location);
  const resolvedClosedMessage = (closedMessage || invitation.closedMessage || DEFAULT_CLOSED_MESSAGE).trim()
    || DEFAULT_CLOSED_MESSAGE;
  const resolvedSuccessMessage = (invitation.successMessage || DEFAULT_SUCCESS_MESSAGE).trim()
    || DEFAULT_SUCCESS_MESSAGE;
  const r = (parts) => responsiveClass(viewport, parts);

  const titleScale = {
    compact: 'text-lg',
    normal: r({
      mobile: 'text-xl',
      desktop: 'text-2xl',
      auto: 'text-xl md:text-2xl',
    }),
    large: r({
      mobile: 'text-2xl',
      desktop: 'text-3xl',
      auto: 'text-2xl md:text-3xl',
    }),
  }[theme.fontScale] || r({
    mobile: 'text-xl',
    desktop: 'text-2xl',
    auto: 'text-xl md:text-2xl',
  });

  const buttonRadius = {
    rounded: 'rounded-lg',
    pill: 'rounded-full',
    square: 'rounded-none',
  }[theme.buttonStyle] || 'rounded-lg';

  const layout = theme.layout || 'classic';

  const formPad = r({
    mobile: 'rounded-xl border p-5',
    desktop: 'rounded-xl border p-6',
    auto: 'rounded-xl border p-5 md:p-6',
  });

  const layoutStyles = {
    classic: {
      root: '',
      align: 'text-center',
      logo: 'mx-auto max-h-20 max-w-full object-contain',
      form: formPad,
      formBorder: true,
      showDivider: true,
      metaAlign: 'items-center',
    },
    card: {
      root: r({
        mobile: 'ba-card shadow-md p-6',
        desktop: 'ba-card shadow-md p-8',
        auto: 'ba-card shadow-md p-6 md:p-8',
      }),
      align: 'text-center',
      logo: 'mx-auto max-h-20 max-w-full object-contain',
      form: r({
        mobile: 'rounded-xl border-2 p-5 bg-[var(--ba-surface-bg)]',
        desktop: 'rounded-xl border-2 p-6 bg-[var(--ba-surface-bg)]',
        auto: 'rounded-xl border-2 p-5 md:p-6 bg-[var(--ba-surface-bg)]',
      }),
      formBorder: true,
      showDivider: true,
      metaAlign: 'items-center',
    },
    minimal: {
      root: 'mx-auto max-w-lg',
      align: 'text-left',
      logo: 'max-h-14 max-w-[160px] object-contain',
      form: 'space-y-4 p-0',
      formBorder: false,
      showDivider: false,
      metaAlign: 'items-start',
    },
  }[layout] || {
    root: '',
    align: 'text-center',
    logo: 'mx-auto max-h-20 max-w-full object-contain',
    form: formPad,
    formBorder: true,
    showDivider: true,
    metaAlign: 'items-center',
  };

  const coverStyleMap = {
    full: {
      wrapper: 'mb-5 overflow-hidden rounded-lg',
      image: 'h-auto w-full object-contain',
      placeholder: r({
        mobile: 'flex h-48 w-full items-center justify-center bg-black/[0.06] text-sm text-[var(--ba-muted)]',
        desktop: 'flex h-64 w-full items-center justify-center bg-black/[0.06] text-sm text-[var(--ba-muted)]',
        auto: 'flex h-48 w-full items-center justify-center bg-black/[0.06] text-sm text-[var(--ba-muted)] sm:h-56 md:h-64',
      }),
    },
    contained: {
      wrapper: 'mb-5 flex justify-center',
      image: r({
        mobile: 'max-h-40 max-w-[min(100%,12rem)] rounded-xl border border-[var(--ba-surface-border)] bg-black/[0.03] object-contain p-4',
        desktop: 'max-h-48 max-w-[14rem] rounded-xl border border-[var(--ba-surface-border)] bg-black/[0.03] object-contain p-4',
        auto: 'max-h-40 max-w-[min(100%,12rem)] rounded-xl border border-[var(--ba-surface-border)] bg-black/[0.03] object-contain p-4 sm:max-h-48 sm:max-w-[14rem]',
      }),
      placeholder: 'flex h-36 w-48 max-w-full items-center justify-center rounded-xl border border-dashed border-[var(--ba-surface-border)] bg-black/[0.03] text-sm text-[var(--ba-muted)]',
    },
  };

  const activeCoverStyle = theme.coverStyle !== 'none'
    ? coverStyleMap[theme.coverStyle] || coverStyleMap.full
    : null;

  const renderFieldInput = (rawField, idx) => {
    const field = normalizeField(rawField);
    const fieldId = `preview_field_${field.name}_${idx}`;
    const defaultValue = field.type === 'select' && field.multiple ? [] : field.type === 'checkbox' ? false : '';
    const value = formValues[field.name] ?? defaultValue;
    const requiredAttr = field.required && !readOnly ? { required: true } : {};
    const disabledAttr = readOnly ? { disabled: true } : {};

    if (mode === 'preview' && !readOnly) {
      if (field.type === 'select') {
        return (
          <div className="ba-input flex min-h-[40px] items-center text-[var(--ba-muted)]">
            {field.multiple ? 'Selezione multipla…' : 'Seleziona un\'opzione…'}
          </div>
        );
      }
      if (field.type === 'checkbox') {
        return (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" disabled className="h-4 w-4" />
            {field.name}
          </label>
        );
      }
      if (field.type === 'textarea') {
        return <div className="ba-input min-h-[88px] text-[var(--ba-muted)]">…</div>;
      }
      return <div className="ba-input min-h-[40px] text-[var(--ba-muted)]">…</div>;
    }

    if (field.type === 'checkbox') {
      return (
        <label className="flex cursor-pointer items-start gap-2 text-sm">
          <input
            type="checkbox"
            id={fieldId}
            className="mt-0.5 h-4 w-4 accent-brand-orange"
            checked={!!value}
            onChange={(e) => onFieldChange?.(field.name, e.target.checked)}
            {...requiredAttr}
            {...disabledAttr}
          />
          <span>
            {field.name}{field.required ? ' *' : ''}
            {field.hint ? <span className="mt-0.5 block text-xs font-normal ba-muted">{field.hint}</span> : null}
          </span>
        </label>
      );
    }

    if (field.type === 'number') {
      return (
        <input
          type="number"
          id={fieldId}
          className="ba-input"
          value={value}
          onChange={(e) => onFieldChange?.(field.name, e.target.value)}
          {...requiredAttr}
          {...disabledAttr}
        />
      );
    }

    if (field.type === 'email') {
      return (
        <input
          type="email"
          id={fieldId}
          className="ba-input"
          value={value}
          onChange={(e) => onFieldChange?.(field.name, e.target.value)}
          {...requiredAttr}
          {...disabledAttr}
        />
      );
    }

    if (field.type === 'tel') {
      return (
        <input
          type="tel"
          id={fieldId}
          className="ba-input"
          value={value}
          onChange={(e) => onFieldChange?.(field.name, e.target.value)}
          {...requiredAttr}
          {...disabledAttr}
        />
      );
    }

    if (field.type === 'date') {
      return (
        <input
          type="date"
          id={fieldId}
          className="ba-input"
          value={value}
          onChange={(e) => onFieldChange?.(field.name, e.target.value)}
          {...requiredAttr}
          {...disabledAttr}
        />
      );
    }

    if (field.type === 'select') {
      const options = getVisibleOptions(field.options);
      if (field.multiple) {
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {options.map((opt) => {
              const label = getOptionLabel(opt);
              return (
                <label key={label} className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-brand-orange"
                    checked={selectedValues.includes(label)}
                    onChange={(e) => onCheckboxChange?.(field.name, label, e.target.checked)}
                    disabled={readOnly}
                  />
                  <span>{label}</span>
                </label>
              );
            })}
          </div>
        );
      }
      return (
        <select
          id={fieldId}
          className="ba-input"
          value={value}
          onChange={(e) => onFieldChange?.(field.name, e.target.value)}
          {...requiredAttr}
          {...disabledAttr}
        >
          <option value="">-- seleziona --</option>
          {options.map((opt) => {
            const label = getOptionLabel(opt);
            return (
              <option key={label} value={label}>{label}</option>
            );
          })}
        </select>
      );
    }

    if (field.type === 'textarea') {
      return (
        <textarea
          id={fieldId}
          className="ba-input min-h-[96px] resize-y"
          rows={4}
          value={value}
          onChange={(e) => onFieldChange?.(field.name, e.target.value)}
          {...requiredAttr}
          {...disabledAttr}
        />
      );
    }

    return (
      <input
        type="text"
        id={fieldId}
        className="ba-input"
        value={value}
        onChange={(e) => onFieldChange?.(field.name, e.target.value)}
        {...requiredAttr}
        {...disabledAttr}
      />
    );
  };

  const renderFormField = (rawField, idx, parentVisible = true) => {
    const field = normalizeField(rawField);
    const visible = parentVisible && isFieldVisible(field, formValues);

    if (field.type === 'section') {
      if (!visible) return null;
      const childFields = (field.fields || []).filter((child) => isFieldVisible(child, formValues));
      if (childFields.length === 0 && mode !== 'preview') return null;

      return (
        <div
          key={field.id || `section-${idx}`}
          className="animate-in fade-in rounded-xl border border-[var(--ba-surface-border)] bg-black/[0.02] p-4 duration-200"
        >
          {field.name && (
            <h4 className="mb-3 text-sm font-semibold" style={{ color: primaryColor }}>
              {field.name}
            </h4>
          )}
          <div className="space-y-4">
            {(field.fields || []).map((child, childIdx) => renderFormField(child, childIdx, visible))}
          </div>
        </div>
      );
    }

    if (!visible) return null;

    return (
      <div
        key={field.id || `${field.name}-${idx}`}
        className="animate-in fade-in duration-200"
      >
        {field.type !== 'checkbox' && (
          <label htmlFor={`preview_field_${field.name}_${idx}`} className="ba-label">
            {field.name}{field.required ? ' *' : ''}
          </label>
        )}
        {field.type !== 'checkbox' && field.hint ? (
          <p className="mb-1.5 text-xs ba-muted">{field.hint}</p>
        ) : null}
        {renderFieldInput(field, idx)}
      </div>
    );
  };

  const summaryData = submittedSummary || (submitted ? formValues : null);
  const summaryFields = getLeafFields(invitation.fields).filter((field) => {
    if (!summaryData) return false;
    return hasSummaryValue(summaryData[field.name]);
  });

  const showBothMetaCards = !!(eventDateLabel && showLocation);
  const metaCardsClass =
    viewport === 'mobile'
      ? 'flex flex-col gap-3'
      : viewport === 'desktop'
        ? showBothMetaCards
          ? 'grid grid-cols-2 gap-3'
          : 'grid grid-cols-1 gap-3'
        : showBothMetaCards
          ? 'grid grid-cols-1 gap-3 sm:grid-cols-2'
          : 'grid grid-cols-1 gap-3';
  const metaCardsStyle =
    viewport === 'mobile'
      ? { display: 'flex', flexDirection: 'column' }
      : viewport === 'desktop' && showBothMetaCards
        ? { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }
        : undefined;

  return (
    <div className={`${layoutStyles.root} ${compact ? 'text-sm' : ''}`}>
      <div className={`mb-6 ${layoutStyles.align}`}>
        {logoSrc ? (
          <img src={logoSrc} alt="Logo" className={layoutStyles.logo} />
        ) : (
          <img src={logo} alt="Logo Banco Alimentare" className={layoutStyles.logo} />
        )}
      </div>

      {showCover && activeCoverStyle && (
        <div className={activeCoverStyle.wrapper}>
          {coverSrc ? (
            <img src={coverSrc} alt="Copertina" className={activeCoverStyle.image} />
          ) : (
            <div className={activeCoverStyle.placeholder}>
              Anteprima copertina ({theme.coverStyle === 'contained' ? 'contenuta' : 'larga'})
            </div>
          )}
        </div>
      )}

      <h2 className={`${titleScale} mb-3 font-bold ${layoutStyles.align}`} style={{ color: primaryColor }}>
        {safeTitle}
      </h2>

      {hasDescription && descriptionIsRich && (
        <div
          className="mb-4 ba-rich-text text-left"
          dangerouslySetInnerHTML={{ __html: safeDescriptionHtml }}
        />
      )}

      {hasDescription && !descriptionIsRich && (
        <p className="mb-4 whitespace-pre-line text-left ba-muted">{safeDescriptionPlain}</p>
      )}

      {(eventDateLabel || showLocation) && (
        <div className="mb-6 space-y-3">
          <div className={metaCardsClass} style={metaCardsStyle}>
            {eventDateLabel && (
              <div className="ba-card flex gap-3 p-4 text-left">
                <div
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: `${primaryColor}1a`, color: primaryColor }}
                  aria-hidden
                >
                  <CalendarClock className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[var(--ba-muted)]">Quando</p>
                  <p className="mt-1 text-sm font-semibold leading-snug text-[var(--ba-body-text)] capitalize">
                    {eventDateLabel}
                  </p>
                </div>
              </div>
            )}

            {showLocation && (
              <div className="ba-card flex gap-3 p-4 text-left">
                <div
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: `${secondaryColor}1a`, color: secondaryColor }}
                  aria-hidden
                >
                  <MapPinned className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[var(--ba-muted)]">Dove</p>
                  <p className="mt-1 text-sm font-semibold leading-snug text-[var(--ba-body-text)]">
                    {location.address || `${location.lat}, ${location.lng}`}
                  </p>
                  {mapsUrl && location.showDirections !== false && (
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-orange transition-colors hover:text-brand-green"
                    >
                      Apri indicazioni
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {locationHasMapMarkers(location) && (
            <div className="ba-card overflow-hidden p-0">
              <InvitationMap
                location={location}
                accentColor={primaryColor}
                className="rounded-none border-0"
                heightClass={compact ? 'h-40' : 'h-48'}
                resizeTrigger={resizeMapTrigger}
              />
            </div>
          )}
        </div>
      )}

      {layoutStyles.showDivider && (
        <hr className="my-5 border-[var(--ba-surface-border)]" />
      )}

      {closed ? (
        <div className="rounded-lg border border-brand-gold/40 bg-brand-gold/10 px-4 py-6 text-center">
          <p className="font-semibold text-brand-gold whitespace-pre-line">{resolvedClosedMessage}</p>
        </div>
      ) : submitted && summaryData ? (
        <div className={r({
          mobile: 'space-y-4 rounded-xl border border-brand-green/30 bg-brand-green/5 p-5',
          desktop: 'space-y-4 rounded-xl border border-brand-green/30 bg-brand-green/5 p-6',
          auto: 'space-y-4 rounded-xl border border-brand-green/30 bg-brand-green/5 p-5 md:p-6',
        })}>
          <div className="text-center">
            <p className="text-lg font-bold text-brand-green whitespace-pre-line">{resolvedSuccessMessage}</p>
            <p className="mt-1 text-sm ba-muted">Ecco un riepilogo dei dati inviati:</p>
          </div>
          <dl className="space-y-2 text-sm">
            {summaryFields.map((field, idx) => (
              <div key={field.id || idx} className="flex justify-between gap-4 border-b border-[var(--ba-surface-border)] pb-2">
                <dt className="font-medium">{field.name}</dt>
                <dd className="text-right whitespace-pre-line">{formatSummaryValue(summaryData[field.name])}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : submitted ? (
        <div className="rounded-lg bg-brand-green/15 px-4 py-3 text-center font-semibold text-brand-green whitespace-pre-line">
          {resolvedSuccessMessage}
        </div>
      ) : (
        <form
          onSubmit={onSubmit}
          className={layoutStyles.form}
          style={layoutStyles.formBorder ? { borderColor: secondaryColor } : undefined}
        >
          <h3 className={`mb-4 text-base font-bold ${layoutStyles.align}`} style={{ color: primaryColor }}>
            Compila i seguenti campi
          </h3>

          <div className="space-y-4">
            {invitation.fields.length === 0 && (
              <p className="text-center ba-muted">Nessun campo richiesto.</p>
            )}
            {invitation.fields.map((field, idx) => renderFormField(field, idx))}
          </div>

          {mode === 'interactive' && (
            <div className={`mt-6 ${layoutStyles.align}`}>
              <button
                type={disableSubmit ? 'button' : 'submit'}
                disabled={disableSubmit}
                className={`px-6 py-2.5 text-sm font-bold uppercase tracking-wide transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 ${buttonRadius}`}
                style={{ backgroundColor: secondaryColor, color: buttonTextColor }}
                title={disableSubmit ? 'Solo anteprima — l\'invio è disabilitato' : undefined}
              >
                {theme.buttonText || 'Partecipa'}
              </button>
              {disableSubmit && (
                <p className="mt-2 text-xs ba-muted">Anteprima: puoi compilare i campi per testare il modulo.</p>
              )}
            </div>
          )}

          {mode === 'preview' && (
            <div className={`mt-6 ${layoutStyles.align}`}>
              <span
                className={`inline-block px-6 py-2.5 text-sm font-bold uppercase tracking-wide ${buttonRadius}`}
                style={{ backgroundColor: secondaryColor, color: buttonTextColor }}
              >
                {theme.buttonText || 'Partecipa'}
              </span>
            </div>
          )}
        </form>
      )}

      <InvitationPrivacyNotice />
    </div>
  );
}

export default InvitationPreview;
